import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "understanding-metric-data"
    }}>{`Understanding Metric Data`}</h1>
    <p>{`The Metric endpoint returns the measured results for each of the Cloud Manager quality gates: Code Quality, Security, Performance, and Experience Audit. Each metric is represented with a JSON object containing the severity, the expected value, the actual value, the result, the comparator used to generate the result, and the metric name.`}</p>
    <p>{`For example, if you look at the code quality metrics, you will see an object like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "id": "7776",
    "severity": "important",
    "passed": true,
    "override": false,
    "actualValue": "50.4",
    "expectedValue": "50",
    "comparator": "GTE",
    "kpi": "coverage"
}
`}</code></pre>
    <p>{`Here, we can see that this is the `}<inlineCode parentName="p">{`coverage`}</inlineCode>{` metric, the expected value is `}<inlineCode parentName="p">{`50`}</inlineCode>{`, the actual value is `}<inlineCode parentName="p">{`50.4`}</inlineCode>{`, to pass the actual value must be greater than or equal to (GTE) the expected value, that the metric passed.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`kpi`}</inlineCode>{` value is a `}<em parentName="p">{`technical`}</em>{` value which will generally need to be translated into a user-facing value. The reference tables below provide mappings between the technical name in the API response and the names/definitions in the `}<a parentName="p" {...{
        "href": "https://www.adobe.com/go/aem_cloud_mgr_testresults_en"
      }}>{`documentation`}</a>{` for each of the quality gates.`}</p>
    <h2 {...{
      "id": "code-quality"
    }}>{`Code Quality`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Technical Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Display Name`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`security_rating`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Code Coverage`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`reliability_rating`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Reliability Rating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sqale_rating`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Maintainability Rating`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`coverage`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Coverage`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`skipped_tests`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Skipped Unit Tests`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`open_issues`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Open Issues`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`duplicated_lines_density`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Duplicated Lines`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "security-testing"
    }}>{`Security Testing`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Technical Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Display Name`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`deserialization_firewall_attach_api_readiness`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Deserialization Firewall Attach API Readiness`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`deserialization_firewall_functional`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Deserialization Firewall Functional`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`deserialization_firewall_loaded`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Deserialization Firewall Loaded`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`authorizable_node_name_generation`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Authorizable Node Name Generation`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`default_login_accounts`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Default Login Accounts`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sling_get_servlet`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Sling Get Servlet`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cq_dispatcher_configuration`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CQ Dispatcher Configuration`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cq_html_library_manager_config`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CQ HTML Library Manager Config`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sling_java_script_handler`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Sling Java Script Handler`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sling_jsp_script_handler`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Sling JSP Script Handler`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sling_referrer_filter`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Sling Referrer Filter`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ssl_configuration`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SSL Configuration`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`user_profile_default_access`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User Profile Default Access`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`crxde_support`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CRXDE Support`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`davex_health_check`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DavEx Health Check`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`example_content_packages`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Example Content Packages`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`wcm_filter_configuration`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`WCM Filters Configuration`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`webdav_health_check`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`WebDAV Health Check`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`web_server_configuration`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Web Server Configuration`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`replication_and_transport_users`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Replication and Transport Users`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "performance-testing"
    }}>{`Performance Testing`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Technical Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Display Name`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`error_rate`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Page Request Error Rate %`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cpu_utilization_rate`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CPU Utilization Rate`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`disk_io_wait_time`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Disk IO Wait Time`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`response_time`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`95 Percentile Response Time`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`peak_resp_time`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Peak Response Time`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`views_per_minute`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Page Views Per Minute`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`disk_bandwidth_util`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Disk Bandwidth Utilization`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`network_bandwidth_util`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Network Bandwidth Utilization`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`requests_per_minute`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Requests Per Minute`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "experience-audit"
    }}>{`Experience Audit`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Technical Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Display Name`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`performance`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Performance Score`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`best-practices`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Best Practices Score`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`accessibility`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Accessibility Score`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`seo`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEO Score`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`pwa`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PWA Score (not displayed)`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      